/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useCallback, useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

import {
	UnreadAction,
	useCommentsDataActions,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { useUnreadComments } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { fg } from '@confluence/feature-gating';

import type {
	useUnreadPanelCommentsMarkInlineCommentsReadMutation as useUnreadPanelCommentsMarkInlineCommentsReadMutation$data,
	useUnreadPanelCommentsMarkInlineCommentsReadMutationVariables as useUnreadPanelCommentsMarkInlineCommentsReadMutation$variables,
} from './__types__/useUnreadPanelCommentsMarkInlineCommentsReadMutation';

const useUnreadPanelCommentsMarkInlineCommentsReadMutation = gql`
	mutation useUnreadPanelCommentsMarkInlineCommentsReadMutation($commentIds: [ID!]!) {
		markCommentsAsRead(input: { commentIds: $commentIds }) {
			success
			errors
		}
	}
`;
export const useUnreadPanelComments = () => {
	const [{ commentsMarkedAsRead }, { clearCommentsMarkedAsRead }] = useCommentsPanel();
	const { updateUnreadStatus } = useCommentsDataActions();
	const [
		{ readCommentsListState, unreadCommentsListState },
		{ updateReadCommentsListState, updateUnreadCommentsListState },
	] = useUnreadComments();

	// TODO: handle error
	const [markInlineCommentsRead] = useMutation<
		useUnreadPanelCommentsMarkInlineCommentsReadMutation$data,
		useUnreadPanelCommentsMarkInlineCommentsReadMutation$variables
	>(useUnreadPanelCommentsMarkInlineCommentsReadMutation);
	// call the mutation to mark the list of comment IDs as read
	const handleMarkCommentsAsRead = useCallback(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (
			Object.keys(commentsMarkedAsRead.inline).length > 0 ||
			Object.keys(commentsMarkedAsRead.general).length > 0
		) {
			const inlineCommentIdsToMarkAsRead = Object.values(commentsMarkedAsRead.inline).reduce<
				string[]
			>((acc, set) => {
				acc.push(...set);
				return acc;
			}, []);

			const generalCommentIdsToMarkAsRead = Object.values(commentsMarkedAsRead.general).reduce<
				string[]
			>((acc, set) => {
				acc.push(...set);
				return acc;
			}, []);

			const commentIdsToMarkAsRead = [
				...inlineCommentIdsToMarkAsRead,
				...generalCommentIdsToMarkAsRead,
			];

			if (!commentIdsToMarkAsRead?.length) {
				return;
			}

			void markInlineCommentsRead({
				variables: { commentIds: commentIdsToMarkAsRead },
			});

			// update our data store to indicate these comments are now read
			updateUnreadStatus(commentsMarkedAsRead, UnreadAction.READ);

			// reset our list of comments to mark as read
			clearCommentsMarkedAsRead();

			// update existing unread and read data stores
			const commentIdsToMarkAsReadSet = new Set(commentIdsToMarkAsRead);
			const filteredUnreadComments = unreadCommentsListState.filter(
				(unreadComment) => !commentIdsToMarkAsReadSet.has(unreadComment.id),
			);
			updateUnreadCommentsListState(() => filteredUnreadComments);

			const newReadCommentsListState = new Set([
				...readCommentsListState,
				...commentIdsToMarkAsRead,
			]);
			updateReadCommentsListState(() => newReadCommentsListState);
		}
	}, [
		markInlineCommentsRead,
		clearCommentsMarkedAsRead,
		commentsMarkedAsRead,
		updateUnreadStatus,
		readCommentsListState,
		unreadCommentsListState,
		updateReadCommentsListState,
		updateUnreadCommentsListState,
	]);

	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		const handleBeforeUnload = () => {
			// Mark comments as read if the user closes the window
			handleMarkCommentsAsRead();
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			// Mark comments as read when the component unmounts
			handleMarkCommentsAsRead();

			window.removeEventListener('beforeunload', handleBeforeUnload);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return useMemo(
		() => ({
			handleMarkCommentsAsRead,
		}),
		[handleMarkCommentsAsRead],
	);
};
